import React, { useState } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { useAuth } from '../context/AuthContext'
import {
  LogOut,
  Menu,
  X,
  LayoutDashboard,
  Calendar,
  FileText,
  Gift,
  HelpCircle,
  Link as LinkIcon,
  LifeBuoy,
  User
} from 'lucide-react'

export default function CompanyLayout() {
  const { user, logout, hasLimitedAccess } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  // Define all navigation items with icons
  const allNavItems = [
    { path: '/company/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/company/timeline', label: 'Timeline', icon: Calendar },
    { path: '/company/guidelines', label: 'Demo Guidelines', icon: FileText },
    { path: '/company/benefits', label: 'Benefits', icon: Gift },
    { path: '/company/faqs', label: 'FAQs', icon: HelpCircle },
    { path: '/company/links', label: 'Links', icon: LinkIcon },
    { path: '/company/help', label: 'Help', icon: LifeBuoy }
  ]

  // Filter navigation items based on user role
  const navItems = hasLimitedAccess
    ? allNavItems.filter(item =>
        item.label === 'Dashboard' ||
        item.label === 'Timeline' ||
        item.label === 'Benefits' ||
        item.label === 'FAQs' ||
        item.label === 'Links' ||
        item.label === 'Help'
      )
    : allNavItems

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/20 to-accent/10 flex flex-col">
      {/* Fixed Header and Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b">
        {/* Header */}
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div
              className="flex items-center gap-2 sm:gap-4 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/company/dashboard')}
            >
              <img
                src="https://www.indiafintech.com/wp-content/uploads/2016/07/logo2.png"
                alt="IFTA Logo"
                className="h-8 sm:h-12 object-contain"
              />
              <div>
                <h1 className="text-base sm:text-xl font-bold text-foreground">IFTA Partner Portal</h1>
              </div>
            </div>

            {/* Desktop User Info and Logout */}
            <div className="hidden md:flex items-center gap-4">
              <span className="text-sm text-muted-foreground">{user?.email}</span>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={toggleMobileMenu}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:block container mx-auto px-4 pb-4">
          <nav>
            <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
              {navItems.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </nav>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-background/95 backdrop-blur-sm">
            <div className="container mx-auto px-4 py-3">
              <nav className="flex flex-col gap-1">
                {navItems.map((item) => {
                  const Icon = item.icon
                  // Add separators to create logical groupings
                  const showSeparator =
                    (item.label === 'Demo Guidelines' || item.label === 'FAQs') &&
                    navItems.some(i => i.label === 'Timeline')

                  return (
                    <React.Fragment key={item.path}>
                      {showSeparator && (
                        <div className="my-1 border-t border-border/30" />
                      )}
                      <NavLink
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                            isActive
                              ? 'bg-primary/10 text-primary shadow-sm'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 hover:translate-x-0.5'
                          }`
                        }
                      >
                        <Icon className="h-5 w-5 flex-shrink-0" />
                        <span>{item.label}</span>
                      </NavLink>
                    </React.Fragment>
                  )
                })}

                {/* User Section */}
                <div className="mt-3 pt-3 border-t border-border/50">
                  <div className="flex items-center gap-3 px-4 py-2 mb-2 rounded-lg bg-muted/30">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground font-medium">Logged in as</p>
                      <p className="text-sm text-foreground font-medium truncate">{user?.email}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLogout}
                    className="w-full justify-start gap-3 h-11 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/20 transition-all duration-200"
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="font-medium">Logout</span>
                  </Button>
                </div>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Content with padding for fixed header */}
      <div className="flex-1 pt-20 md:pt-32">
        {location.pathname === '/company/benefits' ? (
          <Outlet />
        ) : (
          <div className="container mx-auto px-4 py-4 sm:py-6">
            <Outlet />
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-white border-t mt-auto">
        <div className="container mx-auto px-4 py-4">
          <div className="text-center">
            <span className="text-sm text-gray-600">
              © India FinTech Forum. All rights reserved.
            </span>
          </div>
        </div>
      </footer>
    </div>
  )
}